import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {memo, useMemo} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {ROW_BORDER_BOTTOM} from '../../../../constants/borderStyles';
import {EMPTY_CELL_VALUE_BY_MODE} from '../../../../constants/cellPlaceholder';
import {DEFAULT_EMPTY_CELL_VALUE_MODE} from '../../../../constants/defaultEmptyCellValueMode';
import {DEFAULT_TRUNCATED_CELL_HOVER_MODE} from '../../../../constants/defaultTruncatedCellHoverMode';
import {useGridKeyboardNav} from '../../../../context/GridKeyboardNavContext';
import {
  selectHorizontalSeparatorsVisible,
  selectVerticalSeparatorsVisible,
} from '../../../../store/createEagGridStore';
import {useGridSettingsSafe} from '../../../../store/selectors';
import {selectCellResizeState} from '../../../../store/selectors/selectCellResizeState';
import {useActionsDefinition} from '../../../../store/selectors/useActionsDefinition';
import {useEagGridSelector} from '../../../../store/useEagGridSelector';
import type {ActionsAlignment} from '../../../../types/ActionsAlignment';
import type {EagColumn} from '../../../../types/EagColumn';
import type {EagColumnState} from '../../../../types/EagColumnState';
import type {RowData} from '../../../../types/RowData';
import {getPinnedEdgeShadowClass} from '../../../../utils/getPinnedEdgeShadowClass';
import {LineHeightToPx} from '../../../../utils/lineHeightToValue';
import {GridCell} from './components/GridCell/GridCell';
import {RowActions} from './components/RowActions/RowActions';
import {RowSelectCheckbox} from './components/RowSelectCheckbox/RowSelectCheckbox';

// GridRow reads grid settings and separator visibility from the store once,
// then passes verticalSeparators and valueWrapMode to GridCell as props.
// This avoids per-cell store subscriptions for rarely-changing settings —
// 1 subscription per row instead of 1 per cell.

export type GridRowProps = {
  columnStateMap: Map<string, EagColumnState>;
  nonRightPinnedColumns: EagColumn[];
  rightPinnedColumns: EagColumn[];
  visibleColumnIndexMap: Map<string, number>;
  row: RowData;
  isSelected: boolean;
  /**
   * Marks a row for the host, e.g. one with unread messages: red tint, left bar, medium text.
   * Selection wins over the tint only.
   */
  isHighlighted?: boolean;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  onRowContextMenu?: (row: RowData, event: React.MouseEvent) => void;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  isStriped?: boolean;
  rowIndex: number;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  actionColumnWidth?: number;
  isMobile?: boolean;
  disableAllActions?: boolean;
  actionsAlignment?: ActionsAlignment | null;
  /** Mobile only: opens the MobileRowActionSheet for a row (state owned by GridBody). */
  onOpenActionSheet?: (row: RowData) => void;
  // Selection props — controls whether the row renders a RowSelectCheckbox
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  onToggleSelection?: (rowId: string, extendRange?: boolean) => void;
};

function buildRowClassName(
  isSelected: boolean,
  isHighlighted: boolean,
  isStriped: boolean,
  isClickable: boolean
): string {
  // Keep hover and selection blue and distinct from the neutral zebra stripes.
  // Each branch owns its hover shade so stylesheet order cannot override selection.
  // Selection wins over the highlight, which wins over striping.
  // The highlight bar and text weight stay on a selected row. The bar is an inset shadow, so it
  // composes with the pinned-edge shadow on the same cell. With row selection, RowSelectCheckbox
  // draws the bar in its marker strip, which covers the inset shadow.
  return [
    'group/row',
    match({isSelected, isHighlighted, isStriped})
      .with({isSelected: true}, () => 'bg-blue-200 hover:bg-blue-300')
      .with({isHighlighted: true}, () => 'bg-palettes-red-20-100 hover:bg-blue-100')
      .with({isStriped: true}, () => 'bg-[#FAFBFC] hover:bg-blue-100')
      .otherwise(() => 'hover:bg-blue-100'),
    isHighlighted
      ? 'font-medium [--text-weight-default:500] [&>td:first-child]:inset-shadow-[3px_0_0_var(--color-palettes-red-60-100)]'
      : '',
    isClickable ? 'cursor-pointer' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

// memo: rendered N times in list; parent GridBody re-renders on loading/selection
// changes that don't affect most rows.
export const GridRow = memo((props: GridRowProps) => {
  const horizontalSeparators = useEagGridSelector(selectHorizontalSeparatorsVisible);
  const verticalSeparators = useEagGridSelector(selectVerticalSeparatorsVisible);
  const {isResizing, resizingColumn} = useEagGridSelector(selectCellResizeState);
  // Delta-fetch skeletons: subscribed once per row (array identity only changes
  // when a delta fetch starts/settles), passed to cells as a boolean prop.
  const pendingColumnKeys = useEagGridSelector((state) => state.pendingColumnKeys);
  const gridSettings = useGridSettingsSafe();
  const nav = useGridKeyboardNav();
  const focusedColumnIndex = useEagGridSelector((state) =>
    state.focusZone === 'row' && state.focusedRowIndex === props.rowIndex
      ? state.focusedColumnIndex
      : null
  );
  const heightPx = LineHeightToPx[gridSettings.rowHeight];
  const actionsDefinition = useActionsDefinition();
  const valueWrapMode = gridSettings.valueWrapMode;
  const truncatedCellHoverMode =
    gridSettings.truncatedCellHoverMode ?? DEFAULT_TRUNCATED_CELL_HOVER_MODE;
  const emptyCellValue =
    EMPTY_CELL_VALUE_BY_MODE[gridSettings.emptyCellValueMode ?? DEFAULT_EMPTY_CELL_VALUE_MODE];

  // Map column key → index matching the DOM order of td[data-column] cells.
  // This mirrors getFocusableCells() in the provider, which queries td[data-column] in DOM order.
  const columnIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    let colIdx = 0;
    for (const c of props.nonRightPinnedColumns) {
      map.set(c.key, colIdx++);
    }
    for (const c of props.rightPinnedColumns) {
      map.set(c.key, colIdx++);
    }
    return map;
  }, [props.nonRightPinnedColumns, props.rightPinnedColumns]);

  const handleClick = (event: React.MouseEvent) => {
    if (props.onRowClick) {
      props.onRowClick(props.row, event);
    }
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    if (props.onRowContextMenu) {
      props.onRowContextMenu(props.row, event);
    }
  };

  const isHighlighted = props.isHighlighted ?? false;

  const rowClassName = buildRowClassName(
    props.isSelected,
    isHighlighted,
    props.isStriped ?? false,
    isNotNil(props.onRowClick)
  );

  function renderCell(column: EagColumn, globalIndex: number) {
    const columnState = props.columnStateMap.get(column.key);
    if (!columnState) {
      return null;
    }

    const isLastPinnedLeft = props.lastPinnedLeftKey === column.key;
    const isFirstPinnedRight = props.firstPinnedRightKey === column.key;
    const pinnedEdgeShadowClass = getPinnedEdgeShadowClass(
      columnState.pinned,
      isLastPinnedLeft || isFirstPinnedRight
    );
    const isLastColumn = globalIndex === props.visibleColumnIndexMap.size - 1;

    const isCellFocused =
      focusedColumnIndex !== null && columnIndexMap.get(column.key) === focusedColumnIndex;

    return (
      <GridCell
        key={column.key}
        column={column}
        columnState={columnState}
        row={props.row}
        pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)}
        pinnedRightOffset={props.pinnedRightOffsets.get(column.key)}
        verticalSeparators={verticalSeparators}
        valueWrapMode={valueWrapMode}
        truncatedCellHoverMode={truncatedCellHoverMode}
        emptyCellValue={emptyCellValue}
        rowHeight={gridSettings.rowHeight}
        pinnedEdgeShadowClass={pinnedEdgeShadowClass}
        isLastBeforeActions={isLastColumn}
        isCellFocused={isCellFocused}
        isBeingResized={isResizing && resizingColumn === column.key}
        isColumnPending={pendingColumnKeys.includes(column.key)}
        horizontalSeparators={horizontalSeparators}
        isMobile={props.isMobile ?? false}
        isRowSelected={props.isSelected}
        isRowHighlighted={isHighlighted}
        isLastPinnedLeft={isLastPinnedLeft}
      />
    );
  }

  return (
    <tr
      ref={(el) => nav?.registerRow(props.rowIndex, el)}
      className={rowClassName}
      style={{
        height: heightPx,
        // Skip paint/layout for offscreen rows. `auto <h>px` lets the browser use the
        // real width from table layout and falls back to <h>px before first render.
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${heightPx}px`,
      }}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      aria-rowindex={props.rowIndex}
      // Identifies the row for useRowClickFallback: aria-rowindex cannot be used for
      // that, because a refetch can put a different row at the same index between the
      // press and the release, which would open the wrong entity.
      data-row-id={props.row.id}
    >
      {/* checkbox pseudo-column: rendered before data cells when selection is enabled */}
      <Show when={props.rowSelectMode !== 'NONE' && props.onToggleSelection}>
        {(onToggleSelection) => (
          <RowSelectCheckbox
            isSelected={props.isSelected}
            isHighlighted={isHighlighted}
            rowId={props.row.id}
            onToggleSelection={onToggleSelection}
            rowIndex={props.rowIndex}
            horizontalSeparators={horizontalSeparators}
          />
        )}
      </Show>
      {/* left-pinned + unpinned cells */}
      {props.nonRightPinnedColumns.map((column) =>
        renderCell(column, props.visibleColumnIndexMap.get(column.key) ?? 0)
      )}
      {/* filler td: absorbs remaining space between unpinned and right-pinned columns */}
      <td style={horizontalSeparators ? ROW_BORDER_BOTTOM : undefined} />
      {/* right-pinned cells */}
      {props.rightPinnedColumns.map((column) =>
        renderCell(column, props.visibleColumnIndexMap.get(column.key) ?? 0)
      )}
      {/* action column */}
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {actionsDefinition && props.onActionClick && props.actionColumnWidth ? (
        <RowActions
          row={props.row}
          rowIndex={props.rowIndex}
          actionsDefinition={actionsDefinition}
          onActionClick={props.onActionClick}
          isMobile={props.isMobile ?? false}
          rowHeight={gridSettings.rowHeight}
          actionColumnWidth={props.actionColumnWidth}
          heightPx={heightPx}
          isSelected={props.isSelected}
          isHighlighted={isHighlighted}
          isStriped={props.isStriped}
          isDisabled={props.disableAllActions}
          actionsAlignment={props.actionsAlignment}
          horizontalSeparators={horizontalSeparators}
          onOpenActionSheet={props.onOpenActionSheet}
        />
      ) : null}
    </tr>
  );
});
